import {
    AccountTreeRounded,
    AddRounded,
    CloseRounded,
    WarningAmberRounded,
} from "@mui/icons-material";
import {
    Box,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useStore } from "utils/useStore";
import { ChainSourceStore } from "../../Stores/ChainSourceStore";
import {
    ChainConditionOperator,
    ChainRunCondition,
    ChainSourceModel,
    operatorTakesValue,
    summarizeOutputNames,
} from "../../Stores/ChainSourceHelpers";
import { LLMClassifierBuilderStepperStore } from "../../Stores/LLMClassifierBuilderStepperStore";
import { LLMVariableValueField } from "../LLMVariableValueField";
import { useRemoveParentConfirmation } from "./useRemoveParentConfirmation";
import {
    AndSeparator,
    ChainFieldLabel,
    ChainModelIcon,
    NewBadge,
    VariableToken,
} from "./ChainSourceCommon";

/** Describes a parent in a row or node, e.g. `emits ContactType`. */
export function describeModel(model: ChainSourceModel) {
    const output = `emits ${summarizeOutputNames(
        model.variables.map((variable) => variable.shortName),
    )}`;

    // Labelled by when the implementation was created, since the stored version
    // number isn't as meaningful, and modified time moves on
    // publish and unpublish while creation time stays fixed to the version.
    return model.versionLabel ? `v${model.versionLabel} · ${output}` : output;
}

const chainSourceSectionId = "chain-source-dependencies";

/** Gates this model on the output of upstream models. */
export const ChainSourceSection = observer(function () {
    const store = useStore(ChainSourceStore);
    // Links from other steps (e.g. Volume Control) open and scroll to this section on arrival.
    const [isRevealed] = useState(() => store.isRevealRequested);

    useEffect(() => {
        if (!isRevealed) return;
        store.clearRevealRequest();
        document
            .getElementById(chainSourceSectionId)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [isRevealed, store]);

    return (
        <TitledSection
            id={chainSourceSectionId}
            variant="bordered"
            collapsible
            defaultCollapsed={!isRevealed}
            title={
                <Stack direction="row" columnGap={1} alignItems="center">
                    Dependencies
                    <NewBadge />
                </Stack>
            }
            subtitle="Run this model on the output of upstream Model Builder models. Reuse preconditions instead of re-deriving them."
        >
            <Stack direction="column" rowGap={3} width="100%">
                <ParentModels />
                <DeclaredInputs />
                <RunConditions />
                <ChainSourceFooter />
            </Stack>
        </TitledSection>
    );
});

const ParentModels = observer(function () {
    const store = useStore(ChainSourceStore);
    const removal = useRemoveParentConfirmation();

    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const hasAddableModels = store.addableModelGroups.length > 0;

    return (
        <Stack direction="column" rowGap={1}>
            <ChainFieldLabel
                label="Parent models"
                description="Gate this model on one or more upstream models' outputs. Search any model in your tenant, or run it as an entry point. Each parent is a level above this model."
            />
            {store.parents.map((parent) => (
                <ParentModelRow
                    key={parent.id}
                    parent={parent}
                    onRemove={removal.requestRemove}
                />
            ))}
            {removal.confirmationDialog}
            {!isPickerOpen && (
                <AcxButton
                    color="white"
                    containerStyle={{ width: "fit-content" }}
                    disabled={
                        !hasAddableModels && !store.isLoadingAvailableModels
                    }
                    disabledTooltip="No other models in your tenant are available to add as parents."
                    loading={store.isLoadingAvailableModels}
                    onClick={() => setIsPickerOpen(true)}
                    sx={{ width: "fit-content" }}
                >
                    <AddRounded fontSize="small" sx={{ mr: 0.5 }} />
                    Add parent model
                </AcxButton>
            )}
            {isPickerOpen && (
                <Stack direction="row" columnGap={1} alignItems="center">
                    <Box width="100%" maxWidth="420px">
                        <AcxSelectSingle<ChainSourceModel>
                            id="chain-source-parent-model"
                            fullWidth
                            isLoading={store.isLoadingAvailableModels}
                            labelField="name"
                            valueField="id"
                            placeholder="Search models in your tenant..."
                            options={store.addableModelGroups}
                            onChange={(model) => {
                                store.addParent(model);
                                setIsPickerOpen(false);
                            }}
                        />
                    </Box>
                    <AcxButton
                        color="white"
                        onClick={() => setIsPickerOpen(false)}
                    >
                        Cancel
                    </AcxButton>
                </Stack>
            )}
        </Stack>
    );
});

const ParentModelRow = observer(function (props: {
    parent: ChainSourceModel;
    onRemove: (parentId: string) => void;
}) {
    const { parent, onRemove } = props;

    return (
        <Stack
            direction="row"
            columnGap={1.5}
            alignItems="center"
            sx={(theme) => ({
                backgroundColor: theme.palette.primary[50],
                border: `1px solid ${theme.palette.primary[200]}`,
                borderRadius: "6px",
                paddingBlock: theme.spacing(1),
                paddingInline: theme.spacing(1.5),
            })}
        >
            <ChainModelIcon variant="parent" />
            <Typography
                sx={(theme) => ({
                    color: theme.palette.gray[700],
                    fontSize: "14px",
                    fontWeight: 600,
                    // Model names can be long, so the name shrinks with the
                    // row rather than forcing it wider than the card.
                    minWidth: 0,
                })}
            >
                {parent.name}
            </Typography>
            <Tooltip
                title={parent.variables
                    .map((variable) => variable.name)
                    .join(", ")}
            >
                <Typography
                    sx={(theme) => ({
                        color: theme.palette.gray[500],
                        fontSize: "12px",
                        // A structured output lists more names than fit, so the
                        // summary clips and the tooltip carries the full set.
                        flexShrink: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    })}
                >
                    {describeModel(parent)}
                </Typography>
            </Tooltip>
            <Box flexGrow={1} minWidth={0} />
            <Tooltip title="Remove parent model">
                <IconButton size="small" onClick={() => onRemove(parent.id)}>
                    <CloseRounded fontSize="small" sx={{ color: "gray.500" }} />
                </IconButton>
            </Tooltip>
        </Stack>
    );
});

const DeclaredInputs = observer(function () {
    const store = useStore(ChainSourceStore);
    const removal = useRemoveParentConfirmation();

    return (
        <Stack direction="column" rowGap={1}>
            <ChainFieldLabel
                label="Declared inputs"
                isNew
                description={
                    <>
                        Referenced in the prompt like{" "}
                        <VariableToken name="transcript" />. A parent's output
                        becomes a named input.
                    </>
                }
            />
            <Stack direction="row" columnGap={1} rowGap={1} flexWrap="wrap">
                {store.declaredInputGroups.map((group) => (
                    <AcxChip
                        key={group.key}
                        size="small"
                        color={group.parentId ? "primary800" : "lightGray"}
                        label={`${group.name} · ${group.detail}`}
                        sx={{ maxWidth: "100%" }}
                        customTooltip={
                            group.fullDetail
                                ? `${group.name} · ${group.fullDetail}`
                                : undefined
                        }
                        customMaterialIcon={
                            group.parentId ? (
                                <AccountTreeRounded sx={{ fontSize: "12px" }} />
                            ) : undefined
                        }
                        // Removing a chip removes the parent model itself,
                        // which is why inputs group per parent, not per output.
                        onDelete={
                            group.parentId
                                ? () => removal.requestRemove(group.parentId!)
                                : undefined
                        }
                    />
                ))}
            </Stack>
            {removal.confirmationDialog}
        </Stack>
    );
});

const RunConditions = observer(function () {
    const store = useStore(ChainSourceStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);

    return (
        <Stack direction="column" rowGap={1}>
            <ChainFieldLabel
                label="Run conditions"
                isNew
                description="Parents on the same level are peers, one level above this model. This model runs only when every one of their conditions passes."
            />
            <Box
                sx={(theme) => ({
                    border: `1px solid ${theme.palette.lightgrayBorder.main}`,
                    borderRadius: "6px",
                    paddingBlock: theme.spacing(1.5),
                    paddingInline: theme.spacing(2),
                })}
            >
                {store.parents.length === 0 && (
                    <Typography
                        sx={(theme) => ({
                            color: theme.palette.gray[500],
                            fontSize: "13px",
                        })}
                    >
                        No parent models. This model runs as an entry point on
                        every conversation in scope.
                    </Typography>
                )}
                {/* Only after leaving the step has been attempted -- an empty
                    value is not a mistake until then. */}
                {store.hasIncompleteRunConditions &&
                    stepper.hasError.chainSourceRunConditionMissing && (
                        <Typography
                            sx={(theme) => ({
                                color: theme.palette.error.main,
                                fontSize: "12px",
                                paddingBottom: theme.spacing(1.5),
                            })}
                        >
                            {store.incompleteParents.length === 1
                                ? "1 parent still needs a value"
                                : `${store.incompleteParents.length} parents still need a value`}{" "}
                            before you can continue.
                        </Typography>
                    )}
                {store.parentLevels.map(({ level, parents }) => (
                    <React.Fragment key={level}>
                        {parents.map((parent, index) => (
                            <React.Fragment key={parent.id}>
                                {/* Peers on a level are ANDed, so the separator
                                    goes between them. Levels run in order and
                                    need none. */}
                                {index > 0 && (
                                    <Box marginBlock={1.5}>
                                        <AndSeparator />
                                    </Box>
                                )}
                                <ParentRunConditionRows parent={parent} />
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}
            </Box>
        </Stack>
    );
});

/**
 * Schema output names are long and have nothing to break on, so the menu and the
 * collapsed control both size to `max-content`. `auto` would only shrink-to-fit
 * the control, since react-select positions the menu and the selected value
 * absolutely -- and an absolute value leaves the control nothing to measure at
 * all. Max widths keep a given name from running off the panel, where
 * breaking anywhere takes over.
 */
const outputSelectStyle = {
    menu: (provided) => ({
        ...provided,
        width: "max-content",
        minWidth: "100%",
        maxWidth: "min(24rem, 70vw)",
    }),
    option: (provided) => ({
        ...provided,
        overflowWrap: "anywhere",
    }),
    singleValue: (provided) => ({
        ...provided,
        position: "static",
        transform: "none",
        // The default reserves 8px for the absolute positioning, which is enough
        // to ellipsize the name the control was just sized to.
        maxWidth: "100%",
    }),
    // The value shares a line with the search input, under a fixed height.
    valueContainer: (provided) => ({ ...provided, flexWrap: "nowrap" }),
};

/**
 * A parent that publishes no outputs, which only happens once its variables have
 * gone away. It still runs first, since the relation orders execution, but there
 * is nothing left to gate on. Saying so beats a blank level, and names what can
 * be removed above if the parent is no longer wanted.
 */
const UngatedParentRow = observer(function (props: {
    parent: ChainSourceModel;
}) {
    const store = useStore(ChainSourceStore);
    const { parent } = props;

    return (
        <Stack
            direction="row"
            columnGap={1}
            alignItems="center"
            flexWrap="wrap"
        >
            <AcxChip
                size="small"
                color="lightBlue"
                label={`Level ${store.getLevel(parent.id)}`}
            />
            <Typography
                sx={(theme) => ({
                    color: theme.palette.gray[700],
                    fontSize: "13px",
                    fontWeight: 600,
                    minWidth: 0,
                    overflowWrap: "anywhere",
                })}
            >
                {parent.name}
            </Typography>
            <Typography
                sx={(theme) => ({
                    color: theme.palette.gray[500],
                    fontSize: "12px",
                    minWidth: 0,
                    overflowWrap: "anywhere",
                })}
            >
                publishes no outputs. This model runs after it without a
                condition.
            </Typography>
        </Stack>
    );
});

const ParentRunConditionRows = observer(function (props: {
    parent: ChainSourceModel;
}) {
    const store = useStore(ChainSourceStore);
    const { parent } = props;
    const conditions = store.getRunConditions(parent.id);

    if (conditions.length === 0) return <UngatedParentRow parent={parent} />;

    return (
        <Stack direction="column" rowGap={1}>
            {conditions.map((condition, index) => (
                <React.Fragment key={condition.id}>
                    {index > 0 && <AndSeparator />}
                    <RunConditionRow
                        parent={parent}
                        condition={condition}
                        showParentHeader={index === 0}
                        canRemove={conditions.length > 1}
                    />
                </React.Fragment>
            ))}
            <AcxButton
                color="white"
                containerStyle={{ width: "fit-content" }}
                startIcon={<AddRounded />}
                onClick={() => store.addRunCondition(parent.id)}
                sx={{ width: "fit-content" }}
            >
                Condition
            </AcxButton>
        </Stack>
    );
});

const RunConditionRow = observer(function (props: {
    parent: ChainSourceModel;
    condition: ChainRunCondition;
    showParentHeader: boolean;
    canRemove: boolean;
}) {
    const store = useStore(ChainSourceStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);
    const { parent, condition, showParentHeader, canRemove } = props;

    // A gate starts without a value, which is expected rather than a mistake, so
    // it only reads as invalid once visited or once the step has been left.
    const [hasBeenVisited, setHasBeenVisited] = useState(false);

    const conditionVariable = store.getConditionVariable(condition.id);
    // The saved operator is always listed. A parent's outputs can change after a
    // gate is saved -- one that gains declared values is compared whole from then
    // on -- and the gate should still read as what it is rather than as empty.
    const operatorOptions = [
        ...new Set([
            ...store.getConditionOperators(condition.id),
            condition.operator,
        ]),
    ].map((operator) => ({ label: operator, value: operator }));
    const isComplete = store.isRunConditionComplete(condition.id);
    const showValueError =
        !isComplete &&
        (hasBeenVisited || stepper.hasError.chainSourceRunConditionMissing);

    return (
        <Stack direction="column" rowGap={1}>
            {showParentHeader && (
                <Stack direction="row" columnGap={1} alignItems="center">
                    <AcxChip
                        size="small"
                        color="lightBlue"
                        label={`Level ${store.getLevel(parent.id)}`}
                    />
                    <Typography
                        sx={(theme) => ({
                            color: theme.palette.gray[700],
                            fontSize: "13px",
                            fontWeight: 600,
                            minWidth: 0,
                            overflowWrap: "anywhere",
                        })}
                    >
                        {parent.name}
                    </Typography>
                </Stack>
            )}
            <Stack
                direction="row"
                columnGap={1}
                rowGap={1}
                alignItems="center"
                flexWrap="wrap"
            >
                <Typography
                    sx={(theme) => ({
                        color: theme.palette.gray[600],
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                    })}
                >
                    run when
                </Typography>
                {/* Most models emit a single output, so there is nothing to
                    pick: show it as text, and offer a dropdown only for models
                    that emit more than one, such as sentiment. */}
                {parent.variables.length > 1 ? (
                    <AcxSelectSingle
                        id={`chain-condition-variable-${condition.id}`}
                        width="max-content"
                        minWidth="180px"
                        labelField="shortName"
                        valueField="name"
                        options={parent.variables}
                        defaultValue={conditionVariable}
                        infoText={conditionVariable?.name}
                        customStyle={outputSelectStyle}
                        onChange={(variable) =>
                            store.setConditionVariable(
                                condition.id,
                                variable.name,
                            )
                        }
                    />
                ) : (
                    <Tooltip title={conditionVariable?.name ?? ""}>
                        <Box
                            sx={(theme) => ({
                                backgroundColor: theme.palette.gray[100],
                                borderRadius: "4px",
                                color: theme.palette.gray[700],
                                fontFamily: "monospace",
                                fontSize: "12px",
                                paddingBlock: theme.spacing(0.5),
                                paddingInline: theme.spacing(1),
                            })}
                        >
                            {conditionVariable?.shortName}
                        </Box>
                    </Tooltip>
                )}
                {/* One operator is not a choice -- a structured output only
                    tests for presence -- so it reads as part of the sentence
                    rather than as a dropdown of one. */}
                {operatorOptions.length > 1 ? (
                    <Box width="160px">
                        <AcxSelectSingle
                            id={`chain-condition-operator-${condition.id}`}
                            fullWidth
                            labelField="label"
                            valueField="value"
                            options={operatorOptions}
                            defaultValue={operatorOptions.find(
                                (option) => option.value === condition.operator,
                            )}
                            onChange={(option) =>
                                store.setConditionOperator(
                                    condition.id,
                                    option.value as ChainConditionOperator,
                                )
                            }
                        />
                    </Box>
                ) : (
                    <Typography
                        sx={(theme) => ({
                            color: theme.palette.gray[600],
                            fontSize: "13px",
                            whiteSpace: "nowrap",
                        })}
                    >
                        {condition.operator}
                    </Typography>
                )}
                {/* "is defined" and "is not defined" test the variable itself,
                    so there is nothing to compare against. */}
                {operatorTakesValue(condition.operator) && (
                    // Blur bubbles in React, so this covers whichever control
                    // renders below.
                    <Box width="200px" onBlur={() => setHasBeenVisited(true)}>
                        <LLMVariableValueField
                            id={`chain-condition-value-${condition.id}`}
                            fullWidth
                            variable={conditionVariable?.source}
                            value={condition.value}
                            error={showValueError}
                            hideErrorIcon
                            onChange={(value) =>
                                store.setConditionValue(condition.id, value)
                            }
                        />
                    </Box>
                )}
                {canRemove && (
                    <Tooltip title="Remove condition">
                        <IconButton
                            size="small"
                            onClick={() =>
                                store.removeRunCondition(condition.id)
                            }
                        >
                            <CloseRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Stack>
    );
});

const ChainSourceFooter = observer(function () {
    const store = useStore(ChainSourceStore);

    const dependentCount = store.dependents.length;

    // Both halves of the footer are conditional, so without either one the
    // divider would hang below the section with nothing under it.
    if (store.restrictedParents.length === 0 && dependentCount === 0) {
        return null;
    }

    return (
        <Stack direction="column" rowGap={1.5}>
            <Divider />
            <Stack
                direction="row"
                columnGap={2}
                rowGap={1}
                alignItems="center"
                flexWrap="wrap"
            >
                {store.restrictedParents.map((parent) => (
                    <AcxChip
                        key={parent.id}
                        size="small"
                        color="yellow"
                        label={`${parent.name} runs on a subset of conversations`}
                        customMaterialIcon={
                            <WarningAmberRounded sx={{ fontSize: "12px" }} />
                        }
                    />
                ))}
                {dependentCount > 0 && (
                    <Stack direction="row" columnGap={0.75} alignItems="center">
                        {/* Purple to match the downstream nodes in the model
                            chain, so the dependents here read as the same thing. */}
                        <AccountTreeRounded
                            sx={(theme) => ({
                                color: theme.palette.purple[500],
                                fontSize: "14px",
                            })}
                        />
                        <Typography
                            sx={(theme) => ({
                                color: theme.palette.gray[500],
                                fontSize: "12px",
                            })}
                        >
                            <Box
                                component="span"
                                sx={(theme) => ({
                                    color: theme.palette.gray[700],
                                    fontWeight: 600,
                                })}
                            >
                                {dependentCount} model
                                {dependentCount === 1 ? "" : "s"}
                            </Box>{" "}
                            {dependentCount === 1 ? "depends" : "depend"} on
                            this:{" "}
                            {store.dependents
                                .map((dependent) => dependent.name)
                                .join(", ")}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
});
